import { Box, Flex, Text, useToken } from '@chakra-ui/react'
import { useMemo } from 'react'
import type { TooltipContentProps } from 'recharts'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import { useIsDarkMode } from '../hooks/useIsDarkMode'
import { useReportingI18n } from '../i18n'
import { ShowMoreButton } from '../shared/ShowMoreButton'
import type { ExtensionData } from '../types'
import { formatDuration } from '../utils/formatter'
import ExtensionComparisonChartBatch from './ExtensionComparisonChartBatch'

const RENDER_BATCH = 25

interface ExtensionComparisonChartProps {
  data: ExtensionData[]
  hasMore?: boolean
  remaining?: number
  onShowMore?: () => void
}

const ExtensionComparisonChart = ({
  data,
  hasMore,
  remaining,
  onShowMore,
}: ExtensionComparisonChartProps) => {
  const { text } = useReportingI18n()

  const isDark = useIsDarkMode()

  const barColors = useToken('colors', [
    'blue.400',
    'orange.400',
    'pink.400',
    'green.400',
    'purple.400',
  ])

  const lightColors = useToken('colors', [
    'gray.500',
    'gray.400',
    'gray.700',
    'gray.400',
    'gray.300',
    'gray.700',
  ])

  const darkColors = useToken('colors', [
    'gray.400',
    'gray.600',
    'gray.300',
    'gray.600',
    'gray.700',
    'gray.300',
  ])

  const [xTickFill, xStroke, yTickFill, yStroke, gridStroke, labelFill] = isDark
    ? darkColors
    : lightColors

  const chartColors = useMemo(
    () => ({ xTickFill, xStroke, yTickFill, yStroke, gridStroke, labelFill }),
    [xTickFill, xStroke, yTickFill, yStroke, gridStroke, labelFill],
  )

  const callsBatches = useMemo(() => {
    const result: ExtensionData[][] = []
    for (let i = 0; i < data.length; i += RENDER_BATCH) {
      result.push(data.slice(i, i + RENDER_BATCH))
    }
    return result
  }, [data])

  const minutesBatches = useMemo(() => {
    const result: ExtensionData[][] = []
    for (let i = 0; i < data.length; i += RENDER_BATCH) {
      result.push(data.slice(i, i + RENDER_BATCH))
    }
    return result
  }, [data])

  const maxCalls = useMemo(() => {
    if (data.length === 0) return 100
    return Math.max(...data.map((item) => item.totalCalls))
  }, [data])

  const maxMinutes = useMemo(() => {
    if (data.length === 0) return 100
    return Math.max(...data.map((item) => item.totalMinutes))
  }, [data])

  const CallsTooltip = useMemo(
    () =>
      ({ active, payload }: TooltipContentProps) => {
        if (active && payload && payload.length) {
          const item = payload[0].payload as ExtensionData
          return (
            <Flex
              direction="column"
              bg="bg.default"
              border="1px solid"
              borderColor="border.default"
              borderRadius="4px"
              p="8px"
              boxShadow="md"
            >
              <Flex
                {...textStyles[textStyleKeys.Body1_Bold]}
                color="fg.default"
                mb="4px"
              >
                {item.displayLabel}
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                justifyContent="space-between"
                width="100%"
              >
                <Text color="fg.default">
                  {text('extensionComparisonChart.totalCalls', 'Total Calls')}:
                </Text>
                <Text ml="8px" color="fg.info">
                  {item.totalCalls.toLocaleString()}
                </Text>
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                justifyContent="space-between"
                width="100%"
              >
                <Text color="fg.default">
                  {text(
                    'extensionComparisonChart.avgDuration',
                    'Average Duration',
                  )}
                  :
                </Text>
                <Text ml="8px" color="fg.muted">
                  {formatDuration(item.averageDuration)}
                </Text>
              </Flex>
            </Flex>
          )
        }
        return null
      },
    [text],
  )

  const MinutesTooltip = useMemo(
    () =>
      ({ active, payload }: TooltipContentProps) => {
        if (active && payload && payload.length) {
          const item = payload[0].payload as ExtensionData
          return (
            <Flex
              direction="column"
              bg="bg.default"
              border="1px solid"
              borderColor="border.default"
              borderRadius="4px"
              p="8px"
              boxShadow="md"
            >
              <Flex
                {...textStyles[textStyleKeys.Body1_Bold]}
                color="fg.default"
                mb="4px"
              >
                {item.displayLabel}
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                justifyContent="space-between"
                width="100%"
              >
                <Text color="fg.default">
                  {text(
                    'extensionComparisonChart.totalMinutes',
                    'Total Minutes',
                  )}
                  :
                </Text>
                <Text ml="8px" color="fg.info">
                  {item.totalMinutes.toLocaleString()}
                </Text>
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                justifyContent="space-between"
                width="100%"
              >
                <Text color="fg.default">
                  {text('extensionComparisonChart.totalCalls', 'Total Calls')}:
                </Text>
                <Text ml="8px" color="fg.muted">
                  {item.totalCalls.toLocaleString()}
                </Text>
              </Flex>
            </Flex>
          )
        }
        return null
      },
    [text],
  )

  return (
    <Box width="100%">
      <Text
        {...textStyles[textStyleKeys.Body2_Bold]}
        color="fg.default"
        mb="0.5rem"
      >
        {text('extensionComparisonChart.totalCallsTitle', 'Total Calls')}
      </Text>
      {callsBatches.map((batch, batchIndex) => (
        <ExtensionComparisonChartBatch
          key={`calls-batch-${batchIndex}`}
          data={batch}
          dataKey="totalCalls"
          startIndex={batchIndex * RENDER_BATCH}
          barColors={barColors}
          chartColors={chartColors}
          CustomTooltip={CallsTooltip}
          isLast={batchIndex === callsBatches.length - 1}
          isFirst={batchIndex === 0}
          maxValue={maxCalls}
        />
      ))}
      {hasMore && onShowMore && (
        <ShowMoreButton remaining={remaining ?? 0} onClick={onShowMore} />
      )}

      <Text
        {...textStyles[textStyleKeys.Body2_Bold]}
        color="fg.default"
        mb="0.5rem"
        mt="1.5rem"
      >
        {text('extensionComparisonChart.totalMinutesTitle', 'Total Minutes')}
      </Text>
      {minutesBatches.map((batch, batchIndex) => (
        <ExtensionComparisonChartBatch
          key={`minutes-batch-${batchIndex}`}
          data={batch}
          dataKey="totalMinutes"
          startIndex={batchIndex * RENDER_BATCH}
          barColors={barColors}
          chartColors={chartColors}
          CustomTooltip={MinutesTooltip}
          isLast={batchIndex === minutesBatches.length - 1}
          isFirst={batchIndex === 0}
          maxValue={maxMinutes}
        />
      ))}
      {hasMore && onShowMore && (
        <ShowMoreButton remaining={remaining ?? 0} onClick={onShowMore} />
      )}
    </Box>
  )
}

export default ExtensionComparisonChart
